/**
 * Angled orthographic projection.
 *
 * DESIGN.md section 5, and the reason is thematic rather than technical.
 * Perspective projection implies a viewer standing somewhere. Orthographic has
 * no vanishing point and no privileged position: it is the view of a system,
 * not a person. It also renders detection coverage at constant scale across
 * the whole map, and a plate reader's coverage is a fixed geometric fact that
 * a perspective camera would misrepresent.
 *
 * Isometric is one particular orthographic angle, the one where the three axes
 * foreshorten equally. Any angled orthographic works here. The constraint that
 * matters is orthographic, not the exact angle.
 */

import { TILT_DEG, YAW_DEG } from './constants.ts';

export interface Projection {
  /** Degrees away from straight down. 0 is a plan view. */
  readonly tiltDeg: number;
  /** Rotation of the ground plane about the vertical axis, in degrees. */
  readonly yawDeg: number;
  readonly cosYaw: number;
  readonly sinYaw: number;
  /** North-south foreshortening, cos(tilt). */
  readonly yScale: number;
  /** How much a meter of height rises on screen, sin(tilt). */
  readonly zScale: number;
}

export function makeProjection(tiltDeg = TILT_DEG, yawDeg = YAW_DEG): Projection {
  const tilt = (tiltDeg * Math.PI) / 180;
  const yaw = (yawDeg * Math.PI) / 180;
  return {
    tiltDeg,
    yawDeg,
    cosYaw: Math.cos(yaw),
    sinYaw: Math.sin(yaw),
    yScale: Math.cos(tilt),
    zScale: Math.sin(tilt),
  };
}

export interface ScreenPoint {
  sx: number;
  sy: number;
}

/**
 * World meters to projected units. Screen y grows downward, world y grows
 * north, hence the sign flip.
 */
export function project(p: Projection, x: number, y: number, z = 0): ScreenPoint {
  const rx = x * p.cosYaw - y * p.sinYaw;
  const ry = x * p.sinYaw + y * p.cosYaw;
  return { sx: rx, sy: -ry * p.yScale - z * p.zScale };
}

/**
 * Projected units back to world meters on the ground plane (z = 0). Used for
 * turning a mouse position into a place on the map.
 */
export function unproject(p: Projection, sx: number, sy: number): { x: number; y: number } {
  const rx = sx;
  const ry = -sy / p.yScale;
  return {
    x: rx * p.cosYaw + ry * p.sinYaw,
    y: -rx * p.sinYaw + ry * p.cosYaw,
  };
}
